import React, { useState } from 'react';
import {
  House,
  CalendarBlank,
  Plus,
  Users,
} from '@thinkific/chalk/react-icons';
import {
  goToHome,
  goToEvents,
  goToNewSpace,
  goToSpaces,
  goToMembers,
  pathnames,
} from 'utils/routerHelpers';
import { useTranslation } from 'react-i18next';
import { Button, MenuItem } from '@thinkific/chalk';
import { ExpandableMenuSection } from '@thinkific/chalk/recipes/ExpandableMenuSection';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { canAccess, canViewMemberList } from 'utils/userHelpers';
import { USER_ROLES } from 'utils/constants';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useRouter } from 'next/router';
import { useSpaces } from 'components/new/SpacesProvider/SpacesProvider';
import { rawId } from 'utils/rawId';
import SpacesItem from './SpacesItem';
import * as css from './Sidebar.css';

export const Sidebar = ({ community, trackEvent, currentUser }) => {
  const { t } = useTranslation();
  const [sectionsExpanded, setSectionsExpanded] = useState(true);
  const canAddSpace = canAccess(
    [
      USER_ROLES.SITE_OWNER_SITE_ADMIN,
      USER_ROLES.SITE_ADMIN,
      USER_ROLES.PARTNER,
    ],
    currentUser
  );
  const { spaceList, onSpaceDragEnd } = useSpaces();
  const router = useRouter();

  const handleCreateSpaceClick = () => {
    trackEvent(TrackingEvent.GO_TO_NEW_SPACE, {
      [TrackingProperty.COMPONENT]: 'SpacesList',
    });
    goToNewSpace(router, community.id);
  };

  const handleSpaceClick = (filter: string, name: string) => {
    trackEvent(TrackingEvent.SPACE_CLICKED, {
      [TrackingProperty.COMMUNITY_ID]: rawId(community.id),
      [TrackingProperty.SPACE_ID]: rawId(filter),
      [TrackingProperty.SPACE_NAME]: name,
    });

    goToSpaces(router, filter, community.id);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  return (
    <ul className={css.container} data-qa="sidebar">
      <MenuItem
        active={router.pathname === pathnames.home}
        data-qa="home-menu-item"
        icon={House}
        label={t('pages-home_title', 'Home')}
        onClick={() => goToHome(router, community.id)}
      >
        {t('pages-home_title', 'Home')}
      </MenuItem>
      <MenuItem
        active={router.pathname === pathnames.events.events}
        data-qa="events-menu-item"
        icon={CalendarBlank}
        label={t('pages-events_title', 'Events')}
        onClick={() => {
          trackEvent(TrackingEvent.CLICK_SEE_ALL_EVENTS);
          goToEvents(router, community.id);
        }}
      >
        {t('pages-events_title', 'Events')}
      </MenuItem>
      {canViewMemberList(currentUser) && (
        <MenuItem
          active={router.pathname === pathnames.members}
          data-qa="members-menu-item"
          icon={Users}
          label={t('pages-members_title', 'Members')}
          onClick={() => {
            trackEvent(TrackingEvent.CLICK_ALL_MEMBERS_PAGE);
            goToMembers(router, community.id);
          }}
        >
          {t('pages-members_title', 'Members')}
        </MenuItem>
      )}
      <div className={css.spacesContainer}>
        <ExpandableMenuSection
          open={sectionsExpanded}
          title={t('communities_sidebar-spaces', 'Spaces')}
          titleItem={
            canAddSpace ? (
              <Button
                emphasis="lowCompact"
                icon={Plus}
                size="sm"
                hiddenLabel
                onClick={() => handleCreateSpaceClick()}
              >
                {t('communities_sidebar-spaces-create', 'Create Space')}
              </Button>
            ) : null
          }
          onToggle={() => setSectionsExpanded(!sectionsExpanded)}
        >
          <DndContext
            collisionDetection={closestCenter}
            sensors={sensors}
            onDragEnd={onSpaceDragEnd}
          >
            <ul className={css.spaceList}>
              <SortableContext
                items={spaceList}
                strategy={verticalListSortingStrategy}
              >
                {spaceList.map((space) => (
                  <SpacesItem
                    goToFunction={handleSpaceClick}
                    key={space.id}
                    space={space}
                  />
                ))}
              </SortableContext>
            </ul>
          </DndContext>
        </ExpandableMenuSection>
      </div>
    </ul>
  );
};

export default Sidebar;
